import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, MonthCalendar } from '../../../components'
import {
  WEEKDAY_LABELS_WORKWEEK, MONTH_LABELS,
  startOfWeek, addDays, addMonths, isSameDay, dateOnly, formatTime,
  monthGridRange, monthLabel,
} from '../../../components/MonthCalendar'
import { fetchCalendarProviderItems, asCalendarEntry } from '../../../helpers'
import { useMessage } from '../../../hooks'
import { eventService } from '../services'
import { appName } from '../appConfig'
import { swedishHolidayName, isWeekendDay } from '../helpers/swedishHolidays'
import '../index.css'

// Working hours shown in the work-week time grid.
const WORK_START_HOUR = 8
const WORK_END_HOUR = 17
const WORK_HOURS = Array.from({ length: WORK_END_HOUR - WORK_START_HOUR }, (_, i) => WORK_START_HOUR + i)
const MIN_BLOCK_FRACTION = 0.04 // keep very short/edge events visible and clickable

// An event's effective end, for both display and geometry — an event with no
// end date is treated as lasting 1 hour, purely for sizing (never written back).
const effectiveEnd = (event) => event.event_end_timestamp
  ? new Date(event.event_end_timestamp)
  : new Date(new Date(event.event_start_timestamp).getTime() + 60 * 60 * 1000)

// Fraction of the working-hours span (8:00–17:00) a given moment falls at,
// clamped to [0, 1] — a time before 8:00 clamps to 0, after 17:00 clamps to 1.
const hourFraction = (date) => {
  const hours = date.getHours() + date.getMinutes() / 60
  const clamped = Math.min(Math.max(hours, WORK_START_HOUR), WORK_END_HOUR)
  return (clamped - WORK_START_HOUR) / (WORK_END_HOUR - WORK_START_HOUR)
}

// Vertical position/size of an event's block on a specific day of its span:
// the first day shows the real start time through end-of-day, the last day
// shows start-of-day through the real end time, and any day in between shows
// the full working day — the event genuinely occupies all of it.
const blockGeometryForDay = (event, day) => {
  const start = new Date(event.event_start_timestamp)
  const end = effectiveEnd(event)
  const isFirstDay = isSameDay(day, start)
  const isLastDay = isSameDay(day, end)

  let top, bottom
  if (isFirstDay && isLastDay) {
    top = hourFraction(start)
    bottom = hourFraction(end)
  } else if (isFirstDay) {
    top = hourFraction(start)
    bottom = 1
  } else if (isLastDay) {
    top = 0
    bottom = hourFraction(end)
  } else {
    top = 0
    bottom = 1
  }

  if (bottom - top < MIN_BLOCK_FRACTION) bottom = Math.min(1, top + MIN_BLOCK_FRACTION)

  return { top: `${top * 100}%`, height: `${(bottom - top) * 100}%` }
}

const EventCalendar = () => {
  const navigate = useNavigate()
  const { showMessage } = useMessage()

  const [mode, setMode] = useState('week') // 'week' | 'month'
  const [anchor, setAnchor] = useState(() => new Date())
  const [events, setEvents] = useState([])
  // Items contributed by other apps (recruitment interviews, Kayenta Stories, …) via their
  // appDefinition's calendarEvents provider. Kept in their own state so a failing provider
  // can never take this app's own events down with it.
  const [providerEntries, setProviderEntries] = useState([])
  const [loading, setLoading] = useState(true)

  // The visible date range and the days to render as columns/cells — computed
  // fresh whenever the mode or anchor date changes.
  const { rangeStart, rangeEnd, days, label } = useMemo(() => {
    if (mode === 'week') {
      const start = startOfWeek(anchor)
      const end = addDays(start, 5) // Mon..Fri inclusive, exclusive end = Saturday
      const days = Array.from({ length: 5 }, (_, i) => addDays(start, i))
      const last = days[days.length - 1]
      const label = start.getMonth() === last.getMonth()
        ? `${start.getDate()}–${last.getDate()} ${MONTH_LABELS[start.getMonth()]} ${start.getFullYear()}`
        : `${start.getDate()} ${MONTH_LABELS[start.getMonth()]} – ${last.getDate()} ${MONTH_LABELS[last.getMonth()]} ${last.getFullYear()}`
      return { rangeStart: start, rangeEnd: end, days, label }
    }

    // The month grid renders whole weeks, so the fetch range is the grid's, not the
    // month's — MonthCalendar derives the same range from the anchor. `days` is unused in
    // month mode (the shared grid computes its own cells).
    const { start, end } = monthGridRange(anchor)
    return { rangeStart: start, rangeEnd: end, days: [], label: monthLabel(anchor) }
  }, [mode, anchor])

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    eventService.getByRange(rangeStart, rangeEnd, ctrl.signal)
      .then(res => { setEvents(res.events || []); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda events', 'error'); setLoading(false) } })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart.getTime(), rangeEnd.getTime()])

  // Other apps' items for the same window, normalized into this calendar's own entry shape
  // so one set of renderers draws everything. Not awaited together with the events fetch:
  // this calendar is the events app's, and its own rows must appear even if an aggregated
  // provider is slow or broken.
  useEffect(() => {
    let cancelled = false
    fetchCalendarProviderItems(rangeStart, rangeEnd)
      .then(items => { if (!cancelled) setProviderEntries(items.map(asCalendarEntry)) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart.getTime(), rangeEnd.getTime()])

  const entries = useMemo(() => [...events, ...providerEntries], [events, providerEntries])

  // A day cell/column shows the event if `day` falls anywhere within its
  // span (compared at day granularity, ignoring time-of-day) so a multi-day
  // event shows on every day between its start and end — unless the event
  // has opted out of weekends, in which case Saturday/Sunday are skipped.
  // (No-op for the work-week view, which has no weekend columns anyway.)
  const eventSpansDay = (event, day) => {
    const start = dateOnly(new Date(event.event_start_timestamp))
    const end = dateOnly(effectiveEnd(event))
    const dayOnly = dateOnly(day)
    if (dayOnly < start || dayOnly > end) return false

    if (event.event_include_weekends === false) {
      const weekday = day.getDay() // 0=Sun, 6=Sat
      if (weekday === 0 || weekday === 6) return false
    }

    return true
  }

  const eventsForDay = (day, list = entries) => list
    .filter(e => eventSpansDay(e, day))
    .sort((a, b) => new Date(a.event_start_timestamp) - new Date(b.event_start_timestamp))

  // An aggregated item with no time of day is a *date*, not an appointment (a Kayenta
  // Stories item is exactly that). The work-week grid is a time grid: such an item would be
  // drawn as a sliver pinned to 08:00, claiming a precision it doesn't have. It shows in
  // month mode, which is day-granular, and is left out here. This app's own events always
  // carry a real start time, so they're never affected.
  const hasTimeOfDay = (entry) => {
    const d = new Date(entry.event_start_timestamp)
    return d.getHours() !== 0 || d.getMinutes() !== 0
  }
  const weekEntries = useMemo(() => entries.filter(e => !e.external || hasTimeOfDay(e)), [entries])

  // Non-working days get a diagonal-stripe treatment in both views — in the
  // work-week view that's only ever a holiday (weekends have no column
  // there), in the month view it's a weekend or a holiday. Holidays get a
  // red stripe (distinct from the grey weekend one) even when a holiday
  // happens to fall on a weekend (e.g. Midsommardagen is always a Saturday).
  const nonWorkingLabel = (day) => swedishHolidayName(day) || (isWeekendDay(day) ? 'Helg' : null)
  const nonWorkingClass = (day) => swedishHolidayName(day) ? ' holiday' : (isWeekendDay(day) ? ' non-working' : '')

  const goPrev = () => setAnchor(prev => mode === 'week' ? addDays(prev, -7) : addMonths(prev, -1))
  const goNext = () => setAnchor(prev => mode === 'week' ? addDays(prev, 7) : addMonths(prev, 1))
  const goToday = () => setAnchor(new Date())

  const today = new Date()

  // An aggregated item belongs to another app: it links into that app rather than this
  // one's editor, and carries its source colour as a left stripe so the calendar reads as
  // mixed rather than as if the events app suddenly owns interviews.
  const openEntry = (entry) => navigate(entry.external ? entry.url : `/${appName}/${entry.event_slug}/edit`)
  const entryStripe = (entry) => (entry.external ? { borderLeft: `3px solid ${entry.sourceColor}` } : undefined)
  const entryTitle = (entry) => (entry.external ? `${entry.event_title} · ${entry.sourceLabel}` : entry.event_title)

  const renderChip = (event) => (
    <div
      key={event.event_slug}
      className="month-cal-chip"
      style={entryStripe(event)}
      onClick={() => openEntry(event)}
      title={entryTitle(event)}
    >
      <span className="month-cal-chip-time">{formatTime(event.event_start_timestamp)}</span>
      <span className="month-cal-chip-title">{event.event_title}</span>
    </div>
  )

  const renderBlock = (event, day) => {
    const { top, height } = blockGeometryForDay(event, day)
    return (
      <div
        key={event.event_slug}
        className="ev-cal-block"
        style={{ top, height, ...(event.external ? { borderLeftColor: event.sourceColor } : {}) }}
        onClick={() => openEntry(event)}
        title={entryTitle(event)}
      >
        <span className="ev-cal-block-title">{event.event_title}</span>
      </div>
    )
  }

  return (
    <div className="ev-page">
      <div className="ev-page-header">
        <h1>Kalender</h1>
        <div className="ev-page-header-actions">
          <Button value="+ nytt event" className="button main-button" onClick={() => navigate(`/${appName}/new`)} />
        </div>
      </div>

      <div className="ev-cal-toolbar">
        <div className="ev-cal-mode-switch">
          <button type="button" className={"ev-cal-mode-btn" + (mode === 'week' ? ' active' : '')} onClick={() => setMode('week')}>Arbetsvecka</button>
          <button type="button" className={"ev-cal-mode-btn" + (mode === 'month' ? ' active' : '')} onClick={() => setMode('month')}>Månad</button>
        </div>

        <div className="ev-cal-nav">
          <button type="button" className="ev-icon-btn" onClick={goPrev} aria-label="Föregående">‹</button>
          <div className="ev-cal-label">{label}</div>
          <button type="button" className="ev-icon-btn" onClick={goNext} aria-label="Nästa">›</button>
          <Button value="Idag" className="button" onClick={goToday} />
        </div>
      </div>

      {loading
        ? <p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p>
        : mode === 'week'
          ? (
            <div className="ev-cal-week">
              <div className="ev-cal-week-hours">
                <div className="ev-cal-week-col-header" />
                {WORK_HOURS.map(h => (
                  <div className="ev-cal-week-hour-label" key={h}>{String(h).padStart(2, '0')}:00</div>
                ))}
                <div className="ev-cal-week-hour-label ev-cal-week-hour-label-end">{WORK_END_HOUR}:00</div>
              </div>
              {days.map(day => {
                const holidayName = nonWorkingLabel(day)
                return (
                  <div className={"ev-cal-week-col" + (isSameDay(day, today) ? " today" : "")} key={day.toISOString()}>
                    <div className="ev-cal-week-col-header">
                      <span className="ev-cal-weekday">{WEEKDAY_LABELS_WORKWEEK[day.getDay() - 1]}</span>
                      <span className="ev-cal-daynum">{day.getDate()}</span>
                    </div>
                    <div className={"ev-cal-week-col-body" + nonWorkingClass(day)} title={holidayName || undefined}>
                      {WORK_HOURS.map(h => <div className="ev-cal-week-hour-row" key={h} />)}
                      {eventsForDay(day, weekEntries).map(event => renderBlock(event, day))}
                    </div>
                  </div>
                )
              })}
            </div>
          )
          : (
            <MonthCalendar
              anchor={anchor}
              items={events}
              itemsForDay={(day) => eventsForDay(day)}
              renderItem={renderChip}
              dayClassName={(day) => nonWorkingClass(day).trim()}
              dayTitle={(day) => nonWorkingLabel(day)}
            />
          )
      }
    </div>
  )
}

export default EventCalendar
